import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Container,
  Button,
} from "@mui/material";
import { useSelectedProvider } from "../contexts/SelectedProviderContext";

/**
 * Wraps the business-profile page tree. Behavior:
 *  - While loading the list of providers: spinner
 *  - On error: friendly error + retry
 *  - 0 providers: "no business" empty state
 *  - 1 provider: auto-selected upstream in the context; render children
 *  - 2+ providers, no selection yet: render the picker
 *  - Selection made: render children
 *
 * Children may opt into a "switch business" affordance at the top of the
 * page by reading `selectedProvider` and `setSelectedProviderId(null)` from
 * useSelectedProvider().
 */
export default function BusinessProfileGate({ children }) {
  const {
    providers,
    selectedProvider,
    setSelectedProviderId,
    refreshProviders,
    loading,
    error,
  } = useSelectedProvider();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, textAlign: "center" }}>
        <Typography variant="h6" gutterBottom>
          We couldn't load your businesses.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Please try again. If the problem persists, contact help@paircare.ai.
        </Typography>
        <Button variant="contained" onClick={refreshProviders}>
          Try Again
        </Button>
      </Container>
    );
  }

  if (providers.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, textAlign: "center" }}>
        <Typography variant="h6" gutterBottom>
          You don't have a business profile yet.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          If you received an invitation email, click the link in the email to
          claim your business.
        </Typography>
      </Container>
    );
  }

  if (!selectedProvider) {
    // Multi-provider account that hasn't picked yet.
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Which business would you like to manage?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You can switch businesses at any time from this profile page.
        </Typography>
        <Card variant="outlined">
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <List disablePadding>
              {providers.map((p) => (
                <ListItemButton
                  key={p.id}
                  onClick={() => setSelectedProviderId(p.id)}
                  data-testid={`select-provider-${p.id}`}
                >
                  <ListItemText
                    primary={p.name || "(unnamed business)"}
                    secondary={[p.address, p.city, p.state].filter(Boolean).join(", ") || null}
                  />
                </ListItemButton>
              ))}
            </List>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return children;
}
